import { createPanelRef, DashboardResource, LayoutDefinition } from '../../core';
import { PanelGroupDefinition, PanelGroupId, useDashboardStore } from './DashboardProvider';
import { useEventStoreContext } from './Events';
import {
  useSavedViewsDefinition,
  useTemplateVariableActions,
  useTemplateVariableDefinitions,
} from './TemplateVariableProvider';

export function useDashboard() {
  const {
    panels,
    panelGroups,
    panelGroupOrder,
    setDashboard: setDashboardResource,
    display,
    duration,
    time,
    specVersion,
  } = useDashboardStore(
    ({ panels, panelGroups, panelGroupOrder, setDashboard, display, duration, time, specVersion }) => ({
      panels,
      panelGroups,
      panelGroupOrder,
      setDashboard,
      display,
      duration,
      time,
      specVersion,
    })
  );
  const { setEventDefinitions, eventDefinitions } = useEventStoreContext();
  const { setVariableDefinitions, setSavedViewsDefinition } = useTemplateVariableActions();
  const variables = useTemplateVariableDefinitions();
  const savedViews = useSavedViewsDefinition();
  const layouts = convertPanelGroupsToLayouts(panelGroups, panelGroupOrder);

  const dashboard: DashboardResource = {
    kind: 'Dashboard',
    spec: {
      events: eventDefinitions,
      display,
      panels,
      layouts,
      variables,
      duration,
      time,
      saved_views: savedViews,
    },
    spec_version: specVersion,
  };

  const setDashboard = (dashboardResource: DashboardResource) => {
    setEventDefinitions(dashboardResource.spec.events ?? []);
    setVariableDefinitions(dashboardResource.spec.variables);
    setSavedViewsDefinition(dashboardResource.spec.saved_views);
    setDashboardResource(dashboardResource);
  };

  return {
    dashboard,
    setDashboard,
  };
}

function convertPanelGroupsToLayouts(
  panelGroups: Record<number, PanelGroupDefinition>,
  panelGroupOrder: PanelGroupId[]
): LayoutDefinition[] {
  const layouts: LayoutDefinition[] = [];
  panelGroupOrder.map((groupOrderId) => {
    const group = panelGroups[groupOrderId];
    if (group === undefined) {
      throw new Error('panel group not found');
    }
    const { title, isCollapsed } = group;
    let display = undefined;
    let layout: LayoutDefinition | undefined;
    if (title) {
      display = {
        title,
        collapse: {
          open: !isCollapsed,
        },
      };
    }
    if (group.kind === 'Grid') {
      const { itemPanelKeys, itemLayouts } = group;
      layout = {
        kind: 'Grid',
        spec: {
          display,
          items: itemLayouts.map((layout) => {
            const panelKey = itemPanelKeys[layout.i];
            if (panelKey === undefined) {
              throw new Error(`Missing panel key of layout ${layout.i}`);
            }
            return {
              x: layout.x,
              y: layout.y,
              width: layout.w,
              height: layout.h,
              content: createPanelRef(panelKey),
            };
          }),
        },
      };
    } else if (group.kind === 'SplitPanel') {
      layout = {
        kind: 'SplitPanel',
        spec: {
          display,
          content: group.content!,
        },
      };
    }
    if (layout !== undefined) {
      // Split panel group fields are optional, only set when showSplitVariable toggle is true in panel group form.
      if (group.splitVariableName) {
        layout.spec.split_by = {
          variable: group.splitVariableName,
        };
      }
      layouts.push(layout);
    }
  });

  return layouts;
}
